/**
 * Email Editor Merge Tag Field Definitions
 *
 * This file defines the available merge tags for the email editor dropdown.
 * These fields map user-friendly labels to database field names for template replacement.
 *
 * NOTE: This is a temporary email-editor-specific copy.
 * Will be consolidated with other dataFields.js files in a future PR.
 *
 * IMPORTANT — `dataField` must equal the ACTUAL raw column name returned by
 * `/global/components/listings.cfc` (see a SEARCHLISTINGS row), not a "prettied up"
 * guess. listings.cfc's naming is inconsistent on purpose (some fields are
 * snake_case, some are one word, some abbreviate) — do NOT normalize a `dataField`
 * below to match the snake_case pattern of its neighbors without checking a real
 * listings.cfc response first. `resolveAttachedListingMergeTags.ts`'s `resolveByPath`
 * reads this value to fall back to the raw listing row when the camelCase merge-tag
 * key itself isn't a property on the listing object, so a wrong `dataField` here
 * silently breaks that one merge tag (blank output or leaves the raw `{{listing.x}}`
 * tag in place) instead of throwing.
 *
 * The one exception is `image_data`, which arrives as a single column holding a
 * JSON array. `listingsTransport.decodeImageData` turns it into an ordered array
 * of URLs, so those `dataField`s index into that column (`listing.image_data[0]`)
 * rather than naming ten columns that do not exist.
 */

export const dataFields = {
	campaignDisclaimer: {
		label: 'Campaign Disclaimer',
		dataField: 'disclaimer',
	},
	unsubscribeLink: {
		label: 'Unsubscribe Link',
		dataField: 'unsubscribeLink',
	},

	// ========================================
	// LISTING FIELDS - Presentational Data
	// ========================================

	'listing.acreage': {
		label: 'Acreage',
		dataField: 'listing.acreage',
	},
	'listing.address': {
		label: 'Listing Address 1',
		// Raw listings.cfc field is `streetaddress` — there is no `address` column.
		dataField: 'listing.streetaddress',
	},
	'listing.area': {
		label: 'Area',
		dataField: 'listing.area',
	},
	'listing.beds': {
		label: 'Bedrooms',
		dataField: 'listing.beds',
	},
	'listing.city': {
		label: 'City',
		dataField: 'listing.city',
	},
	'listing.communityName': {
		label: 'Community',
		dataField: 'listing.community_name',
	},
	'listing.county': {
		label: 'County',
		dataField: 'listing.county',
	},
	// 'listing.description' is an alias of 'listing.mlsRemarks' (see
	// ALIAS_TO_CANONICAL) — same column, and the spec lists only one name for it.
	'listing.garage': {
		label: 'Garage',
		dataField: 'listing.garage',
	},
	// UNGATED — `lo_comp`/`bo_comp` are the only commission columns that exist
	// (there is no `la_comp`/`ba_comp`), so the mapping below is correct. But the
	// table also carries `lo_comp_display`/`bo_comp_display`, T/F flags controlling
	// whether the agent permits the commission to be shown publicly, and BOTH
	// DEFAULT TO 'F'. `listings.cfc::read()` selects `lo_comp`/`bo_comp` without
	// either flag, so these two tags render a commission the agent may never have
	// authorized for display — in an outbound email, which travels further than the
	// website the flag was written for. Cannot be gated here: the flag is not in the
	// payload. Needs the two `_display` columns added to read()'s projection, then a
	// check before these resolve.
	'listing.listingCommission': {
		label: 'Listing Commission',
		// Raw listings.cfc field is `lo_comp` (listing office comp) — NOT `listing_commission`.
		dataField: 'listing.lo_comp',
	},
	'listing.buyerCommission': {
		label: 'Buyer Commission',
		// Raw listings.cfc field is `bo_comp` (buyer office comp) — NOT `buyer_commission`.
		dataField: 'listing.bo_comp',
	},
	// The Listing Details Page builds "Listing Agent 1" and "Listing Agent 2" from
	// the matched CBAY agent records (publicid / secondpublicid), first + last
	// name — see layout_listings.cfc's agentname1/agentname2. `la_name` is a
	// different field on that same page, labelled "MLS Agent Name", which is why
	// pointing here at la_name returned the wrong person.
	//
	// doQueryFields composes both off the agents lookup it already runs.
	'listing.listingAgent1': {
		label: 'Listing Agent 1',
		dataField: 'listing.agentname',
	},
	'listing.listingAgent2': {
		label: 'Listing Agent 2',
		dataField: 'listing.agentname2',
	},
	'listing.subArea': {
		label: 'Sub Area',
		dataField: 'listing.sub_area',
	},
	'listing.schoolDistrict': {
		label: 'School District',
		dataField: 'listing.school_district',
	},
	'listing.highSchool': {
		label: 'High School',
		dataField: 'listing.high_school',
	},
	'listing.jrHighSchool': {
		label: 'Jr High School',
		dataField: 'listing.jr_high_school',
	},
	'listing.elementarySchool': {
		label: 'Elementary School',
		dataField: 'listing.elementary_school',
	},

	// Image Fields
	'listing.image_data_0': {
		label: 'Image 1',
		dataField: 'listing.image_data[0]',
	},
	'listing.image_data_1': {
		label: 'Image 2',
		dataField: 'listing.image_data[1]',
	},
	'listing.image_data_2': {
		label: 'Image 3',
		dataField: 'listing.image_data[2]',
	},
	'listing.image_data_3': {
		label: 'Image 4',
		dataField: 'listing.image_data[3]',
	},
	'listing.image_data_4': {
		label: 'Image 5',
		dataField: 'listing.image_data[4]',
	},
	'listing.image_data_5': {
		label: 'Image 6',
		dataField: 'listing.image_data[5]',
	},
	'listing.image_data_6': {
		label: 'Image 7',
		dataField: 'listing.image_data[6]',
	},
	'listing.image_data_7': {
		label: 'Image 8',
		dataField: 'listing.image_data[7]',
	},
	'listing.image_data_8': {
		label: 'Image 9',
		dataField: 'listing.image_data[8]',
	},
	'listing.image_data_9': {
		label: 'Image 10',
		dataField: 'listing.image_data[9]',
	},
	'listing.imagePath': {
		label: 'Image Path',
		// Raw listings.cfc field is lowercase `imagepath` — do not camelCase this back.
		dataField: 'listing.imagepath',
	},
	'listing.thumbPath': {
		label: 'Thumbnail Path',
		// Raw listings.cfc field is lowercase `thumbpath` — do not camelCase this back.
		dataField: 'listing.thumbpath',
	},

	// Open House & Media
	'listing.openHouseSchedule': {
		label: 'Open House Schedule',
		dataField: 'listing.openhouseschedule',
	},
	'listing.virtualTourUrl': {
		label: 'Virtual Tour URL',
		dataField: 'listing.virtualtourid',
	},
	'listing.videoTourUrl': {
		label: 'Video Tour URL',
		dataField: 'listing.secondvideotour',
	},

	// Primary Agent Information
	'listing.agentImagePath': {
		label: 'Agent Image',
		dataField: 'listing.agentimagepath',
	},
	'listing.agentFirstName': {
		label: 'Agent First Name',
		dataField: 'listing.agentfirstname',
	},
	'listing.agentLastName': {
		label: 'Agent Last Name',
		dataField: 'listing.agentlastname',
	},
	'listing.agentPhone': {
		label: 'Agent Phone',
		dataField: 'listing.agentphone',
	},
	'listing.agentCell': {
		label: 'Agent Cell',
		dataField: 'listing.agentcell',
	},
	'listing.agentEmail': {
		label: 'Agent Email',
		dataField: 'listing.agentemail',
	},
	'listing.agentWebsiteAddress': {
		label: 'Agent Website',
		dataField: 'listing.agentwebsiteaddress',
	},
	'listing.agentTeam': {
		label: 'Agent Team',
		dataField: 'listing.agentteam',
	},
	'listing.agentCertification': {
		label: 'Agent Certification',
		dataField: 'listing.agentcertification',
	},

	// Second Agent Information
	'listing.secondAgentImagePath': {
		label: 'Second Agent Image',
		dataField: 'listing.secondagentimagepath',
	},
	'listing.secondAgentFirstName': {
		label: 'Second Agent First Name',
		dataField: 'listing.secondagentfirstname',
	},
	'listing.secondAgentLastName': {
		label: 'Second Agent Last Name',
		dataField: 'listing.secondagentlastname',
	},
	'listing.secondAgentPhone': {
		label: 'Second Agent Phone',
		dataField: 'listing.secondagentphone',
	},
	'listing.secondAgentCell': {
		label: 'Second Agent Cell',
		dataField: 'listing.secondagentcell',
	},
	'listing.secondAgentEmail': {
		label: 'Second Agent Email',
		dataField: 'listing.secondagentemail',
	},
	'listing.secondAgentWebsiteAddress': {
		label: 'Second Agent Website',
		dataField: 'listing.secondagentwebsiteaddress',
	},
	'listing.secondAgentCertification': {
		label: 'Second Agent Certification',
		dataField: 'listing.secondagentcertification',
	},
	'listing.secondAgentTeam': {
		label: 'Second Agent Team',
		dataField: 'listing.secondagentteam',
	},

	// Office Information
	'listing.officeName': {
		label: 'Office Name',
		dataField: 'listing.officename',
	},
	'listing.officeAddress': {
		label: 'Office Address',
		dataField: 'listing.officeaddress',
	},
	'listing.officeCity': {
		label: 'Office City',
		dataField: 'listing.officecity',
	},
	'listing.officeState': {
		label: 'Office State',
		dataField: 'listing.officestate',
	},
	'listing.officeZip': {
		label: 'Office Zip',
		dataField: 'listing.officezip',
	},
	'listing.officePhone': {
		label: 'Office Phone',
		dataField: 'listing.officephone',
	},
	// Two logos, not one. `officelogopath` was never a column anywhere — the real
	// ones are Offices.officelogodark / officelogolight (see api/offices.cfc), and
	// the editor already exposes the same dark/light split for the user's own
	// office logo, so listings match it.
	//
	// NOTE: listings.cfc currently selects `'' as officelogodark` — a hardcoded
	// empty placeholder — and has no light equivalent. Both need populating from
	// the Offices join before these resolve to anything.
	'listing.officeLogoDark': {
		label: 'Office Logo Dark',
		dataField: 'listing.officelogodark',
	},
	'listing.officeLogoLight': {
		label: 'Office Logo Light',
		dataField: 'listing.officelogolight',
	},

	// Bathroom Breakdown
	'listing.fullBaths': {
		label: 'Full Bathrooms',
		dataField: 'listing.fullbaths',
	},
	'listing.halfBaths': {
		label: 'Half Bathrooms',
		dataField: 'listing.halfbaths',
	},
	'listing.threeQuarterBaths': {
		label: 'Three Quarter Bathrooms',
		dataField: 'listing.threequarterbaths',
	},

	// Pricing & Details
	'listing.listPrice': {
		label: 'List Price',
		dataField: 'listing.list_price',
	},
	'listing.listPriceSqFoot': {
		label: 'Price per Sq Ft',
		dataField: 'listing.list_price_sq_foot',
	},
	'listing.listingtype': {
		label: 'Listing Type',
		dataField: 'listing.listingtype',
	},
	// There is no lot_size column. The system stores lot size as Acreage —
	// zillow/zillowsold/profitpower/circlepix/cbexport all export
	// `Listings.Acreage as LotSize`.
	'listing.lotsize': {
		label: 'Lot Size',
		dataField: 'listing.acreage',
	},
	'listing.mlsNumber': {
		label: 'MLS Number',
		// Raw listings.cfc field is `ml_number` — NOT `mls_number`.
		dataField: 'listing.ml_number',
	},
	'listing.propertyType': {
		label: 'Property Type',
		// Raw listings.cfc field is one word `propertytype` — NOT `property_type`.
		dataField: 'listing.propertytype',
	},
	'listing.squareFootage': {
		label: 'Square Feet',
		// `SQ_Foot` is aliased to `sq_foot` by listings.cfc — there is no
		// `square_footage` column. This is the canonical key of the two Square Feet
		// tags (see ALIAS_TO_CANONICAL), so it is the one that has to resolve.
		dataField: 'listing.sq_foot',
	},
	// These three are the names on the field spec, so they are what users pick
	// from the menu. They pointed at `baths_total` / `baths_full` / `baths_half`,
	// none of which listings.cfc has — the real columns are `baths`, `fullbaths`
	// and `halfbaths` — so all three rendered a raw {{tag}} in the sent email.
	// The old working twins ('Full Bathrooms' / 'Half Bathrooms') are now aliases
	// onto these, so existing templates keep resolving and the menu stops
	// offering two names for one field.
	'listing.bathsTotal': {
		label: 'Baths Total',
		dataField: 'listing.baths',
	},
	'listing.bathsFull': {
		label: 'Baths Full',
		dataField: 'listing.fullbaths',
	},
	'listing.bathsHalf': {
		label: 'Baths 1/2',
		dataField: 'listing.halfbaths',
	},
	'listing.unitFloor': {
		label: 'Unit Floor',
		dataField: 'listing.unit_floor',
	},
	'listing.suite': {
		label: 'Suite',
		dataField: 'listing.suite',
	},
	'listing.patioSqFt': {
		label: 'Patio Sqft',
		dataField: 'listing.patio_sqft',
	},
	// The field spec sources "MLS Remarks and Description" from the listing's
	// Description field. Both `mls_remarks` and `description` are real columns, so
	// pointing at the wrong one resolves to a real value — it just shows the wrong
	// text, which is why this survived every schema check. 'MLS Description' is
	// the same field under another name and is now an alias.
	'listing.mlsRemarks': {
		label: 'MLS Remarks and Description',
		dataField: 'listing.description',
	},
	// 'listing.sqFoot' is an alias of 'listing.squareFootage' (see
	// ALIAS_TO_CANONICAL) — it was also a full entry here, which is why the menu
	// listed "Square Feet" twice.
	'listing.state': {
		label: 'State / Province',
		dataField: 'listing.state',
	},
	// ponytail: DEAD — no `status` column; `read()` returns `statuscode`.
	// Duplicate of `listing.statusCode`, which resolves correctly. Delete at ship.
	// listings.cfc has no `status` column — the one word `statuscode` is it, which
	// is what 'Status Code' was already reading correctly.
	// The Details Page shows the status dropdown's label ("Active"), not the value
	// stored in statuscode ("active"). doQueryFields composes `listingstatus` as
	// the title-cased form so emails read the same as the page. 'Status Code'
	// below still exposes the raw value.
	'listing.status': {
		label: 'Listing Status',
		dataField: 'listing.listingstatus',
	},
	'listing.statusCode': {
		label: 'Status Code',
		dataField: 'listing.statuscode',
	},
	'listing.style': {
		label: 'Style',
		dataField: 'listing.style',
	},
	'listing.units': {
		label: 'Units',
		dataField: 'listing.units',
	},
	// The `url` column holds a path only. doQueryFields composes `listingurl` as
	// the absolute link — the same protocol://domain + path the Share widget's
	// "Details Page" anchor uses — so the tag is clickable in a sent email.
	'listing.url': {
		label: 'Listing URL',
		dataField: 'listing.listingurl',
	},
	'listing.yearBuilt': {
		label: 'Year Built',
		dataField: 'listing.year_built',
	},
	'listing.zip': {
		label: 'Zip / Postal Code',
		// Raw listings.cfc field is `zip_code` — there is no `zip` column.
		dataField: 'listing.zip_code',
	},
	'listing.zoning': {
		label: 'Zoning',
		dataField: 'listing.zoning',
	},

	// ========================================
	// USER FIELDS
	// ========================================

	'user.websiteUrl': {
		label: 'Website URL',
		dataField: 'user.websiteUrl',
	},
	'user.website': {
		label: 'Website',
		dataField: 'user.website',
	},
	'user.officeDomain': {
		label: 'Office Domain',
		dataField: 'user.officeDomain',
	},
	'user.facebookUrl': {
		label: 'Facebook URL',
		dataField: 'user.facebookUrl',
	},
	'user.instagramUrl': {
		label: 'Instagram URL',
		dataField: 'user.instagramUrl',
	},
	'user.twitterUrl': {
		label: 'X URL',
		dataField: 'user.twitterUrl',
	},
	'user.linkedInUrl': {
		label: 'LinkedIn URL',
		dataField: 'user.linkedInUrl',
	},
	'user.linkedInBusinessUrl': {
		label: 'LinkedIn Business URL',
		dataField: 'user.linkedInBusinessUrl',
	},
	'user.youtubeUrl': {
		label: 'YouTube URL',
		dataField: 'user.youtubeUrl',
	},
	'user.tiktokUrl': {
		label: 'TikTok URL',
		dataField: 'user.tiktokUrl',
	},
	'user.pinterestUrl': {
		label: 'Pinterest URL',
		dataField: 'user.pinterestUrl',
	},
	'user.yelpUrl': {
		label: 'Yelp URL',
		dataField: 'user.yelpUrl',
	},
	'user.email': {
		label: 'Email',
		dataField: 'user.email',
	},
	'user.firstName': {
		label: 'First Name',
		dataField: 'user.firstName',
	},
	'user.lastName': {
		label: 'Last Name',
		dataField: 'user.lastName',
	},
	'user.fullName': {
		label: 'Full Name',
		dataField: 'user.fullName',
	},
	'user.profileImagePortrait': {
		label: 'Profile Image (Portrait)',
		dataField: 'user.profileImagePortrait',
	},
	'user.profileImageSquare': {
		label: 'Profile Image (Square)',
		dataField: 'user.profileImageSquare',
	},
	'user.profileImageCircle': {
		label: 'Profile Image (Circle)',
		dataField: 'user.profileImageCircle',
	},
	'user.companyLogo': {
		label: 'Company Logo',
		dataField: 'user.companyLogo',
	},
	'user.mobilePhone': {
		label: 'Mobile Phone',
		dataField: 'user.mobilePhone',
	},
	'user.primaryPhone': {
		label: 'Direct Phone',
		dataField: 'user.primaryPhone',
	},
	'user.displayPhone': {
		label: 'Display Phone',
		dataField: 'user.displayPhone',
	},
	'user.logoDarkUrl': {
		label: 'Profile Logo Dark',
		dataField: 'user.logoDarkUrl',
	},
	'user.logoLightUrl': {
		label: 'Profile Logo Light',
		dataField: 'user.logoLightUrl',
	},
	'user.officeLogoLightUrl': {
		label: 'Office Logo Light',
		dataField: 'user.officeLogoLightUrl',
	},
	'user.officeLogoDarkUrl': {
		label: 'Office Logo Dark',
		dataField: 'user.officeLogoDarkUrl',
	},
	'user.licenseNumber': {
		label: 'License Number',
		dataField: 'user.licenseNumber',
	},
	'user.companyName': {
		label: 'Company Name',
		dataField: 'user.companyName',
	},
	'user.companyPhone': {
		label: 'Company Phone',
		dataField: 'user.companyPhone',
	},
	'user.certifications': {
		label: 'Certifications',
		dataField: 'user.certifications',
	},
	'user.title': {
		label: 'Title',
		dataField: 'user.title',
	},
	'user.team': {
		label: 'Team',
		dataField: 'user.team',
	},
	'user.officeName': {
		label: 'Office Name',
		dataField: 'user.officeName',
	},
	'user.officeAddress': {
		label: 'Office Address',
		dataField: 'user.officeAddress',
	},
	'user.officeCity': {
		label: 'Office City',
		dataField: 'user.officeCity',
	},
	'user.officeState': {
		label: 'Office State / Province',
		dataField: 'user.officeState',
	},
	'user.officeZip': {
		label: 'Office Zip / Postal Code',
		dataField: 'user.officeZip',
	},

	// ========================================
	// GENERAL DATE / TIME
	// ========================================

	'current_date.day': {
		label: 'Current Day',
		dataField: 'current_date.day',
	},
	'current_date.month': {
		label: 'Current Month',
		dataField: 'current_date.month',
	},
	'current_date.year': {
		label: 'Current Year',
		dataField: 'current_date.year',
	},

	// ========================================
	// CONTACT FIELDS
	// ========================================

	'contact.firstName': {
		label: 'First Name',
		dataField: 'contact.firstName',
	},
	'contact.lastName': {
		label: 'Last Name',
		dataField: 'contact.lastName',
	},
	'contact.email': {
		label: 'Email',
		dataField: 'contact.email',
	},
	'contact.phone': {
		label: 'Phone',
		dataField: 'contact.phone',
	},
	'contact.company': {
		label: 'Company',
		dataField: 'contact.company',
	},
	'contact.address': {
		label: 'Address',
		dataField: 'contact.address',
	},
	'contact.city': {
		label: 'City',
		dataField: 'contact.city',
	},
	'contact.state': {
		label: 'State / Province',
		dataField: 'contact.state',
	},
	'contact.zip': {
		label: 'Zip / Postal Code',
		dataField: 'contact.zip',
	},
	'contact.zipCode': {
		label: 'Zip / Postal Code',
		dataField: 'contact.zipCode',
	},
	'contact.dateOfBirth': {
		label: 'Date of Birth',
		dataField: 'contact.dateOfBirth',
	},
};
